/**
 * The session identity snapshot (phase6-contract.md §3.1 "Session identity
 * snapshot", §3.4 scope key, §3.7 one normaliser).
 *
 * Every identity-keyed local artefact — the persisted realtime cursor, the
 * message outbox scopes, the parked navigation target, the push registration
 * record — is cleared by `{companyKey, userId}`. That pair is captured HERE
 * while the session is authenticated (the realtime service sets it on
 * `bindSession()`, the session store on `applyTokenResponse()` / `restore()`),
 * because the EXPIRY path clears the tokens BEFORE `signOutLocal()` runs: a
 * clear that read the token at clear time would find nothing and leave the
 * artefacts behind for the next user of the device.
 *
 * Memory is the source of truth; `sessionStorage` mirrors it so a page reload
 * that lands on an already-expired token still knows whose artefacts to sweep.
 * No RUNTIME import from any store or service (a leaf — everything imports it);
 * the envelope contract below is a TYPE-only import of a leaf module, so it adds
 * no edge to the module graph.
 */
import type { RealtimeEnvelope } from '@/contracts/realtime-envelope-v1';
import { getSessionUserId, getStoredCompanyKey } from '@/services/api/erpat-client';

export interface SessionScope {
  /** The normalised company key (`normalizeTenant()`), '' when unknown. */
  companyKey: string;
  /** The user id as a decimal string, '' when unknown. */
  userId: string;
}

const STORAGE_KEY = 'syntry.session_scope.v1';

let scope: SessionScope | null = null;

/** THE tenant normaliser: trim + lower-case. Every stamp and comparison uses it. */
export function normalizeTenant(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** The current tenant stamp — `normalizeTenant(getStoredCompanyKey())`. */
export function tenantStamp(): string {
  return normalizeTenant(getStoredCompanyKey());
}

/** Record the identity of the session that is authenticated RIGHT NOW. */
export function setSessionScope(next: { companyKey?: string | null; userId?: string | number | null }): SessionScope {
  scope = {
    companyKey: normalizeTenant(next.companyKey ?? getStoredCompanyKey()),
    userId: next.userId == null || next.userId === '' ? getSessionUserId() : String(next.userId),
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scope));
  } catch {
    /* storage unavailable — memory still holds it */
  }
  return scope;
}

/**
 * The snapshot, or a best-effort reconstruction (the `sessionStorage` mirror,
 * else whatever the token/company key still say). Both fields may be '' — a
 * caller that clears by scope then falls back to a prefix sweep (§3.1, §3.4).
 */
export function getSessionScope(): SessionScope {
  if (scope) return scope;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const rec = parsed as Record<string, unknown>;
        return {
          companyKey: normalizeTenant(typeof rec.companyKey === 'string' ? rec.companyKey : ''),
          userId: typeof rec.userId === 'string' ? rec.userId : '',
        };
      }
    }
  } catch {
    /* fall through */
  }
  return { companyKey: tenantStamp(), userId: getSessionUserId() };
}

/**
 * Is this envelope's actor the signed-in user?
 *
 * True for a SENDER-SYNC `message.created`: ERPat records the stream event for
 * every participant INCLUDING the sender, so the sender's other devices stay in
 * step (`NotificationStreamRecorder::recordMessageCreated()` merges the sender
 * into `$participants`). There is no server-side `socket_id` exclusion — the
 * publisher accepts one but nothing populates it — so suppressing your own
 * traffic is the client's job by contract (phase6-contract.md §3.3).
 *
 * THE one definition: the reducer's unread rule and the service's toast/device
 * rules all read it, so they cannot disagree about who sent a message.
 *
 * An UNKNOWN session (`userId === ''`) is never "me": an unattributed envelope
 * must never be suppressed.
 */
export function isSelfActor(envelope: RealtimeEnvelope): boolean {
  const { userId } = getSessionScope();
  return userId !== '' && envelope.actor !== null && envelope.actor.id === userId;
}

/** Forget the snapshot — the LAST step of a sign-out, after every scoped clear ran. */
export function clearSessionScope(): void {
  scope = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
